import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpEvent, HttpResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Subject, PartialObserver, Subscription, Observable } from 'rxjs';

import {
    IEventPageResponse, IErrorNotification, ISuccessNotification, IApiServiceConfig, IGetIdentityTokenRequest,
    IGetIdentityTokenResponse, IGetEventTokenResponse, IEventTokenPayload, IIdentityTokenPayload, IDependency,
    IGetDependenciesRequest, IGetDependenciesResponse, IPagedRequest, IPagedResponse, IMenuItem, IField,
    IGetEventParticipantFieldsResponse, IGetProcessFormResponse, IGetTokenResponse, IStoredIdentityToken,
    IEventTokenResponse, IStoredEventToken, IApiServiceInitResult, IEventSettings,
    IGetEventParticipantResponse, ICreateEventParticipantResponse, IUpdateEventParticipantResponse,
    ILinkTokenPayload, ICompleteProcessFormResponse, IFieldModeQuery, IGetEventFieldsResponse, IPaymentProductResponse,
    IProductIdWithQuantity, IPayResponse, ICancelProcessResponse, IGetPaymentThankYouPageResponse, IOrder,
    ICheckDiscountCodeResponse, ICheckDiscountCodePayload, IPayProductsPayload, IAsset,
    IGetDocumentDownloadTokenResponse, ISearchProfileEventsResponse, IGetProfileOrdersResponse, IProfilePageResponse,
    ITokenInfo, ISearchEventsPayload, ISearchProfileContactsResponse, ISearchEventActivitiesResponse, IAdvancedSearchRequest,
    IGetActivitiesCartResponse, IRegisterToActivitiesRequest, IContactEmailCampaignSubscriptions, IContactEmailCampaignSubscription, IPaymentIntegrationWebhookRequest, IPayProductsV2Payload, IPayProductsV2CartItem, SortDirection, IImpersonateResponse,
    ParticipantStageActionDenyType
} from './interfaces';
import { ApiUtilsService } from './api-utils.service';
import { IInternalAuthenticateResponse, RequestMethod } from './api-internal.interfaces';
import { EventType, FieldMode } from './enums';
import { DonationProductItem } from '@azavista/payments';
import { RegistrationsRequest, RegistrationsResponse } from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';

export const  API_RESPONSE_CODE_DOCUMENT_NOT_FOUND = 'ObjectNotFound';

@Injectable({
    providedIn: 'root'
})
export class ApiService {

    identityTokensStorageKeyName = 'azavista-identity-tokens';
    identityTokenStorageKeyName = 'azavista-identity-token';
    eventTokensStorageKeyName = 'azavista-event-tokens';
    private lastRequestTime = 0;
    private config: IApiServiceConfig;
    private authorizationHeaderValue = '';
    private errorSubject = new Subject<IErrorNotification>();
    private successSubject = new Subject<ISuccessNotification>();
    private formUrlEncodedHeader = { 'Content-Type': 'application/x-www-form-urlencoded' };
    private identityTokenResponse: IGetTokenResponse;
    private eventTokenResponse: IGetTokenResponse;
    private lastEventId: string;
    private maxTokenExpirationToleranceMs = 2 * 60 * 1000; // 2 minutes before expiration is considered as about to expire
    private storage: Storage = window.localStorage;

    constructor(private http: HttpClient, private utilsSvc: ApiUtilsService) {
    }

    setStorage(storage: Storage): void {
        this.storage = storage;
    }

    init(config: IApiServiceConfig): IApiServiceInitResult {
        this.config = config;
        this.config.baseUrl = this.utilsSvc.addForwardSlash(this.config.baseUrl);
        this.config.contactBaseUrl = this.utilsSvc.addForwardSlash(this.config.contactBaseUrl);
        this.utilsSvc.setConfig(this.config);

        const currentStoredIdentityTokens = this.getStoredIdentityTokens();
        const filteredStoredIdentityTokens = this.filterOutIdentityTokensAboutToExpire(currentStoredIdentityTokens);
        // We are now supporting single identity token only - create new storage key with first found token
        let identityToken: IStoredIdentityToken;
        if (filteredStoredIdentityTokens.length > 0) {
            identityToken = filteredStoredIdentityTokens[0];
            const identityTokenPayload = this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(identityToken.tokenResponse.access_token);
            const isAboutToExpire = this.isTokenAboutToExpire(identityTokenPayload.payload.exp);
            const isForSpecificPage = this.isIdentityTokenForSpecificPage(identityTokenPayload.payload);
            if (!isAboutToExpire && !isForSpecificPage) {
                this.storage.setItem(this.identityTokenStorageKeyName, JSON.stringify(identityToken));
            } else {
                this.removeStoredIdentityToken();
                identityToken = null;
            }
        }
        this.storage.removeItem(this.identityTokensStorageKeyName);
        // Check if we have identity token with page_id - if yes - remove it
        identityToken ||= this.getStoredIdentityToken();
        if (identityToken) {
            const identityTokenPayload = this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(identityToken.tokenResponse.access_token);
            if (this.isIdentityTokenForSpecificPage(identityTokenPayload.payload)) {
                // Identity token has page_id - remove it
                this.removeStoredIdentityToken();
                identityToken = null;
            }
        }

        const currentStoredEventTokens = this.getStoredEventTokens();
        const filteredStoredEventTokens = this.filterOutEventTokensAboutToExpireOrAreForSpecificPage(currentStoredEventTokens);
        this.storage.setItem(this.eventTokensStorageKeyName, JSON.stringify(filteredStoredEventTokens));

        const result: IApiServiceInitResult = {
            identityToken: identityToken, eventTokens: filteredStoredEventTokens
        };
        return result;
    }

    getIdentityTokenInfo(storedIdentityToken: IStoredIdentityToken): ITokenInfo<IIdentityTokenPayload> {
        if (storedIdentityToken?.tokenResponse?.access_token) {
            return this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(storedIdentityToken.tokenResponse.access_token);
        } else {
            return null;
        }
    }

    getEventTokenInfo(storedEventToken: IStoredEventToken): ITokenInfo<IEventTokenPayload> {
        if (storedEventToken?.tokenResponse?.access_token) {
            return this.utilsSvc.getTokenInfo<IEventTokenPayload>(storedEventToken.tokenResponse.access_token);
        } else {
            return null;
        }
    }

    async getContactEmailCampaignSubscriptions(): Promise<IContactEmailCampaignSubscriptions> {
        const path = this.utilsSvc.getContactEmailCampaignSubscriptionsGetUrl();
        const res = await this.get<IContactEmailCampaignSubscriptions>(path, 'getContactEmailCampaignSubscriptions');
        return res;
    }

    async setContactEmailCampaignSubscriptions(subscriptions: IContactEmailCampaignSubscriptions): Promise<void> {
        const path = this.utilsSvc.getContactEmailCampaignSubscriptionsSetUrl();
        const sanitizedSubscriptions = { ...subscriptions };
        sanitizedSubscriptions.subscriptions = sanitizedSubscriptions.subscriptions.map(x => ({
            email_campaign_type_id: x.email_campaign_type_id,
            subscribed: x.subscribed
        } as IContactEmailCampaignSubscription));
        await this.put<void>(path, sanitizedSubscriptions, 'setContactEmailCampaignSubscriptions');
    }

    async registerToActivities(req: IRegisterToActivitiesRequest): Promise<void> {
        const path = this.utilsSvc.getRegisterToActivitiesUrl();
        await this.post<void>(path, req, 'registerToActivities');
    }

    async getActivitiesCart(): Promise<IGetActivitiesCartResponse> {
        const path = this.utilsSvc.getEventActivitiesCartGetUrl();
        const response = await this.get<IGetActivitiesCartResponse>(path, 'getActivitiesCart');
        return response;
    }

    async searchEventActivities(req: IAdvancedSearchRequest): Promise<ISearchEventActivitiesResponse> {
        const path = this.utilsSvc.getEventActivitiesSearchUrl();
        const payload = this.utilsSvc.getSearchEventActivitiesPayload(req.quickSearchText);
        const queryParams = {
            ...this.utilsSvc.sanitizePagedRequest(req),
            activity_display: 'website',
            registration_activities: 'all'
        };
        const response = await this.postWithQuery<ISearchEventActivitiesResponse>(
            path, payload, queryParams, 'searchEventActivities'
        );
        return response;
    }

    async likeActivity(activityId: string): Promise<void> {
        const path = this.utilsSvc.getEventActivityLikeUrl(activityId);
        await this.put<void>(path, null, 'likeActivity');
    }

    async unlikeActivity(activityId: string): Promise<void> {
        const path = this.utilsSvc.getEventActivityUnlikeUrl(activityId);
        await this.put<void>(path, null, 'unlikeActivity');
    }

    async sendConfirmationCode(email: string): Promise<void> {
        const path = this.utilsSvc.getSendConfirmationCodeUrl();
        const body = { email: email };
        const response = await this.post<void>(path, body, 'sendConfirmationCode');
        return response;
    }

    async updateProfileContact(profileContact: { [key: string]: any }): Promise<{ [key: string]: any }> {
        const path = this.utilsSvc.getProfileContactUpdateUrl();
        const response = await this.patch<{ [key: string]: any }>(path, profileContact, 'updateProfileContact');
        return response;
    }

    async profileLogin(email: string, password: string, eventId: string): Promise<IGetEventTokenResponse> {
        const path = this.utilsSvc.getProfileAuthTokenUrl();
        const body = this.utilsSvc.getUrlEndodedAuthObject(email, password);
        const response = await this.postWithHeaders<IInternalAuthenticateResponse>(
            path, body, this.formUrlEncodedHeader, null, 'profileLogin'
        );
        this.setIdentityTokenResponse(response);
        const getTokenResponse = this.processTokenResponse(email, response);
        const eventTokenResponse = await this.getEventToken(eventId, null, false);
        return eventTokenResponse;
    }

    async accessCodeLogin(eventId: string, accessCode: string): Promise<IGetTokenResponse> {
        const path = this.utilsSvc.getAccessCodeLoginUrl(eventId);
        const body = { access_code: accessCode };
        const res = await this.post<IGetTokenResponse>(path, body, 'accessCodeLogin');
        this.setEventTokenResponse(eventId, res);
        return res;
    }

    async sendPasswordResetLink(email: string): Promise<void> {
        const path = this.utilsSvc.getSendPasswordResetLinkUrl();
        const body = { email: email };
        await this.post<void>(path, body, 'sendPasswordResetLink');
    }

    async sendLoginLink(email: string): Promise<void> {
        const path = this.utilsSvc.getSendLoginLinkUrl();
        const body = { email: email };
        await this.post<void>(path, body, 'sendLoginLink');
    }

    async sendCreateAccountLink(email: string): Promise<void> {
        const path = this.utilsSvc.getSendCreateAccountLinkUrl();
        const body = { email: email };
        await this.post<void>(path, body, 'sendCreateAccountLink');
    }

    async getProfileOrders(pagedReq: IPagedRequest): Promise<IGetProfileOrdersResponse> {
        const path = this.utilsSvc.getProfileOrdersGetUrl();
        const res = await this.getWithQuery<IGetProfileOrdersResponse>(path, this.utilsSvc.sanitizePagedRequest(pagedReq), 'getProfileOrders');
        return res;
    }

    async changeProfilePassword(newPassword: string): Promise<void> {
        const path = this.utilsSvc.getProfilePasswordChangeUrl();
        const payload = { password: newPassword };
        await this.put<void>(path, payload, 'changeProfilePassword');
    }

    // async getProfileEvents(pagedReq: IPagedRequest): Promise<IGetProfileEventsResponse> {
    //     const path = this.utilsSvc.getProfileEventsGetUrl();
    //     const res = await this.getWithQuery<IGetProfileEventsResponse>(path, this.utilsSvc.sanitizePagedRequest(pagedReq), 'getProfileEvents');
    //     return res;
    // }
    async searchEvents(
        eventType: EventType, eventSearchText: string, participantSearchText: string,
        myOrganization: boolean, pagedReq: IPagedRequest,
        sortFieldName: string, sortDirection: SortDirection,
        eventId: string | null,
    ): Promise<ISearchProfileEventsResponse> {
        const path = this.utilsSvc.getEventsSearchUrl();
        let payload = this.utilsSvc.getSearchEventsPayload(
            eventSearchText,
            participantSearchText,
            myOrganization,
            sortFieldName,
            sortDirection,
            eventId,
        );
        const query = {
            ...this.utilsSvc.sanitizePagedRequest(pagedReq),
            eventType: eventType
        };
        const res = this.postWithQuery<ISearchProfileEventsResponse>(path, payload, query, 'searchEvents');
        return res;
    }

    async participantStageAction(
        participantID: string,
        stageID: string,
        deny: ParticipantStageActionDenyType
    ): Promise<void> {
        const path = this.utilsSvc.getParticipantStageActionUrl(participantID, stageID);
        const query = {
            deny
        };
        const res = this.postWithQuery<void>(path, {}, query, 'participantStageAction');
        return res;
    }

    async inviteContact(firstName: string, lastName: string, email: string): Promise<void> {
        const path = this.utilsSvc.getContactInviteUrl();
        const payload = {
            first_name: firstName,
            last_name: lastName,
            email: email
        };
        await this.post<void>(path, payload, 'inviteContact');
    }

    async searchContacts(contactSearchText: string, pagedReq: IPagedRequest): Promise<ISearchProfileContactsResponse> {
        const path = this.utilsSvc.getContactsSearchUrl();
        const payload = this.utilsSvc.getSearchContactsPayload(contactSearchText);
        const query = this.utilsSvc.sanitizePagedRequest(pagedReq);
        const res = this.postWithQuery<ISearchProfileContactsResponse>(path, payload, query, 'searchContacts');
        return res;
    }

    async getProfilePage(): Promise<IProfilePageResponse> {
        const path = this.utilsSvc.getProfilePageGetUrl();
        const res = await this.get<IProfilePageResponse>(path, 'getProfilePage');
        return res;
    }

    async getDocumentDownloadToken(documentId: string): Promise<IGetDocumentDownloadTokenResponse> {
        const path = this.utilsSvc.getDocumentDownloadTokenGetUrl(documentId);
        const res = await this.get<IGetDocumentDownloadTokenResponse>(path, 'getDocumentDownloadToken');
        return res;
    }

    getDocumentFileDownloadUrl(documentId: string, token: string): string {
        return this.utilsSvc.getDocumentFileGetUrl(documentId, token);
    }
    // async getDocumentFile(documentId: string): Promise<Blob> {
    //     const path = this.utilsSvc.getDocumentFileGetUrl(documentId);
    //     const req = this.createRequest('GET', path, null, {}, null, 'blob');
    //     const file = await this.getAny<Blob>(path, 'getDocumentFile');
    //     return new Blob([file]);
    // }

    async uploadParticipantDocument(fileName: string, contentType: string, array: Uint8Array): Promise<IAsset> {
        const path = this.utilsSvc.getDocumentUploadUrl();
        const blob = new Blob([array], { type: contentType });
        const queryParams = { filename: fileName };
        const res = await this.postWithQuery<IAsset>(path, blob, queryParams, 'uploadParticipantDocument');
        return res;
    }

    uploadParticipantDocumentFile(file: File): Observable<HttpEvent<IAsset>> {
        const path = this.utilsSvc.getDocumentUploadUrl();
        const queryParams = { filename: file.name };
        const headers = new HttpHeaders({ Authorization: this.authorizationHeaderValue });
        const req = new HttpRequest('POST', path, file, {
            headers: headers,
            reportProgress: true,
            params: new HttpParams({ fromObject: queryParams })
        });
        return this.http.request<IAsset>(req);
    }

    updateDocumentFile(documentId: string, file: File): Observable<HttpEvent<IAsset>> {
        const path = this.utilsSvc.getDocumentUpdateUrl(documentId);
        const queryParams = { filename: file.name };
        const headers = new HttpHeaders({ Authorization: this.authorizationHeaderValue });
        const req = new HttpRequest('PUT', path, file, {
            headers: headers,
            reportProgress: true,
            params: new HttpParams({ fromObject: queryParams })
        });
        return this.http.request<IAsset>(req);
    }

    async updateDocument(documentId: string, fileName: string, contentType: string, array: Uint8Array): Promise<IAsset> {
        const path = this.utilsSvc.getDocumentUpdateUrl(documentId);
        const blob = new Blob([array], { type: contentType });
        const queryParams = { filename: fileName };
        const res = await this.putWithQuery<IAsset>(path, blob, queryParams, 'updateDocument');
        return res;
    }

    async checkDiscountCode(code: string): Promise<ICheckDiscountCodeResponse> {
        const path = this.utilsSvc.getCheckDiscountCodeUrl();
        const payload: ICheckDiscountCodePayload = { discount_code: code };
        const response = await this.post<ICheckDiscountCodeResponse>(path, payload, ' checkDiscountCode');
        return response;
    }

    async paymentIntegrationWebhook(integrationId: string, req: IPaymentIntegrationWebhookRequest): Promise<void> {
        const path = this.utilsSvc.getPaymentIntegrationWebhookUrl(integrationId);
        const body = this.utilsSvc.formUrlEncode(req);
        await this.postWithHeaders<void>(
            path, body, this.formUrlEncodedHeader, null, 'paymentIntegrationWebhook'
        );
    }

    async getPaymentProduct(): Promise<IPaymentProductResponse> {
        const path = this.utilsSvc.getPaymentProductUrl();
        const response = await this.get<IPaymentProductResponse>(path, 'getPaymentProduct');
        return response;
    }

    async payProducts(productsWithQuantities: IProductIdWithQuantity[], discountCode: string, payByInvoice: boolean): Promise<IPayResponse> {
        const path = this.utilsSvc.getPaymentPayUrl();
        const payload: IPayProductsPayload = { products: productsWithQuantities, pay_by_invoice: payByInvoice };
        if (discountCode) {
            payload.discount_code = discountCode;
        }
        const response = await this.put<IPayResponse>(path, payload, 'payProducts');
        return response;
    }

    async payProductsV2(
        productsWithQuantities: IProductIdWithQuantity[],
        donationProducts: DonationProductItem[],
        dependencies: IDependency[],
        discountCode: string | null,
        payByInvoice: boolean
    ): Promise<IPayResponse> {
        const path = this.utilsSvc.getPaymentPayV2Url();
        const payload: IPayProductsV2Payload = {
            cartItems: productsWithQuantities.map(x => ({ product_id: x.product_id, quantity: x.quantity } as IPayProductsV2CartItem)),
            pay_by_invoice: payByInvoice,
            dependencies: dependencies,
            products: donationProducts,
            discount_code: discountCode
        };
        if (!payload.discount_code) {
            delete payload.discount_code;
        }
        if (discountCode) {
            payload.discount_code = discountCode;
        }
        const response = await this.put<IPayResponse>(path, payload, 'payProducts');
        return response;
    }

    async getPaymentThankYouPage(transactionStatus: string): Promise<IGetPaymentThankYouPageResponse> {
        const path = this.utilsSvc.getPaymentThankYouPageUrl();
        const queryParams = { transaction_status: transactionStatus };
        const response = await this.getWithQuery<IGetPaymentThankYouPageResponse>(path, queryParams, 'getPaymentThankYouPage');
        return response;
    }

    async getPaymentOrders(): Promise<IOrder[]> {
        const path = this.utilsSvc.getPaymentOrderGetUrl();
        const response = await this.get<IOrder[]>(path, 'getPaymentOrders', [400]);
        return response;
    }

    async getProcessForm(decline: boolean, additionalQueryParams: any): Promise<IGetProcessFormResponse> {
        const path = this.utilsSvc.getProcessFormGetUrl();
        if (decline || additionalQueryParams) {
            const queryParams = { ...additionalQueryParams };
            if (decline) {
                queryParams.decline = 1;
            }
            const response = await this.getWithQuery<IGetProcessFormResponse>(path, queryParams, 'getProcessForm');
            return response;
        } else {
            const response = await this.get<IGetProcessFormResponse>(path, 'getProcessForm', [404]);
            return response;
        }
    }

    async completeProcessForm(formId: string, queryParams?: Object): Promise<ICompleteProcessFormResponse> {
        const path = this.utilsSvc.getProcessFormCompleteUrl(formId);
        const response = await this.postWithQuery<ICompleteProcessFormResponse>(path, null, queryParams ?? {}, 'completeProcessForm');
        return response;
    }

    async cancelProcess(): Promise<ICancelProcessResponse> {
        const path = this.utilsSvc.getProcessCancelUrl();
        const response = await this.get<ICancelProcessResponse>(path, 'cancelProcess');
        return response;
    }

    async getMultiRegistration(workflow_id: string): Promise<RegistrationsResponse> {
        const path = this.utilsSvc.getMultiRegistrationGetUrl(workflow_id);
        const response = await this.get<RegistrationsResponse>(path, 'getMultiRegistration');
        return response;
    }

    async updateMultiRegistration(workflow_id: string, data: RegistrationsRequest): Promise<void> {
        const path = this.utilsSvc.getMultiRegistrationUpdateUrl(workflow_id);
        const response = await this.put<void>(path, data, 'updateMultiRegistration');
        return response;
    }

    async getMenu(): Promise<IMenuItem[]> {
        const path = this.utilsSvc.getEventMenuGetUrl();
        const response = await this.get<any>(path, 'getMenu', [404]);
        return response;
    }

    async getDependencies(req: IGetDependenciesRequest): Promise<IGetDependenciesResponse> {
        const path = this.utilsSvc.getEventDependenciesGetUrl();
        const response = await this.getPaged<IGetDependenciesResponse>(path, req, 'getDependencies');
        return response;
    }

    async getAllEventDependencies(): Promise<IDependency[]> {
        const dependencies = await this.getAllPagedItems(
            req => this.getDependencies(req),
            res => res.dependencies
        );
        return dependencies;
    }

    async getEventDependencies(): Promise<IDependency[]> {
        const path = this.utilsSvc.getEventDependenciesGetUrl();
        const response = await this.get<IDependency[]>(
            path, 'getEventDepedencies', [404]
        );
        return response;
    }

    async getResetPasswordPage(): Promise<IEventPageResponse> {
        const path = this.utilsSvc.getResetPasswordPageUrl();
        const response = await this.get<IEventPageResponse>(path, 'getResetPasswordPage');
        return response;
    }

    async getEventHomePage(queryParams?: any): Promise<IEventPageResponse> {
        const path = this.utilsSvc.getHomePageGetUrl();
        let response: IEventPageResponse;
        if (!queryParams) {
            response = await this.get<IEventPageResponse>(
                path, 'getEventHomePage', [404]
            );
        } else {
            // TODO: Ignore HTTP errors
            response = await this.getWithQuery<IEventPageResponse>(
                path, queryParams, 'getEventHomePage'
            );
        }
        return response;
    }

    async getEventPage(pageId: string, queryParams?: any): Promise<IEventPageResponse> {
        const path = this.utilsSvc.getPageGetUrl(pageId);
        let response: IEventPageResponse;
        if (!queryParams) {
            response = await this.get<IEventPageResponse>(
                path, 'getEventPage', [404]
            );
        } else {
            // TODO: Ignore HTTP errors
            response = await this.getWithQuery<IEventPageResponse>(
                path, queryParams, 'getEventPage'
            );
        }
        return response;
    }

    private getEventTokenUrl(eventId: string, participantId: string, clearParticipantId: boolean): string {
        let url = this.utilsSvc.getEventTokenGetUrl(eventId);
        const params: string[] = [];
        if (clearParticipantId) {
            params.push(`participant_id=clear`);
        } else if (participantId) {
            params.push(`participant_id=${encodeURIComponent(participantId)}`);
        }

        if (params.length > 0) {
            const paramsText = params.join('&');
            url += `/?${paramsText}`;
        }
        return url;
    }

    async getEventToken(eventId: string, participantId: string, clearParticipantId: boolean): Promise<IGetEventTokenResponse> {
        this.lastEventId = eventId;
        const path = this.getEventTokenUrl(eventId, participantId, clearParticipantId);
        const authHeaderValue = this.utilsSvc.getAuthorizationHeaderFromAccessTokenResponse(this.identityTokenResponse);
        const response = await this.getWithHeaders<IGetTokenResponse>(
            path, { Authorization: authHeaderValue }, 'getEventToken'
        );
        const getEventTokenResponse = this.convertToEventTokenResponseAndSet(eventId, response);
        return getEventTokenResponse;
    }

    async getEventTokenUsingCurrentIdentityToken(eventId: string, participantId: string, clearParticipantId: boolean): Promise<IGetEventTokenResponse> {
        const path = this.getEventTokenUrl(eventId, participantId, clearParticipantId);
        const storedIdentityToken = this.getStoredIdentityToken();
        // TODO: Temporary fix for https://az2019.azavista.com/product-management/frontend/azavista3.0/-/issues/3466
        // const authHeaderValue = this.utilsSvc.getAuthorizationHeaderFromResponse(storedIdentityToken.tokenResponse);
        const authHeaderValue = this.utilsSvc.getAuthorizationHeaderFromRefreshTokenResponse(storedIdentityToken.tokenResponse);
        const response = await this.getWithHeaders<IGetTokenResponse>(
            path, { Authorization: authHeaderValue }, 'getEventTokenUsingCurrentIdentityToken'
        );
        const getEventTokenResponse = this.convertToEventTokenResponseAndSet(eventId, response);
        return getEventTokenResponse;
    }

    async getEventTokenUsingCurrentEventToken(eventId: string, participantId: string, clearParticipantId: boolean): Promise<IGetEventTokenResponse> {
        const path = this.getEventTokenUrl(eventId, participantId, clearParticipantId);
        const storedEventToken = this.getStoredEventToken(eventId);
        let authHeaderValue: string;
        if (storedEventToken) {
            authHeaderValue = this.utilsSvc.getAuthorizationHeaderFromAccessTokenResponse(storedEventToken.tokenResponse);
        } else {
            authHeaderValue = this.authorizationHeaderValue;
        }
        const response = await this.getWithHeaders<IGetTokenResponse>(
            path, { Authorization: authHeaderValue }, 'getEventTokenUsingCurrentEventToken'
        );
        const getEventTokenResponse = this.convertToEventTokenResponseAndSet(eventId, response);
        return getEventTokenResponse;
    }

    async refreshEventToken(eventId: string): Promise<IGetEventTokenResponse> {
        const path = this.utilsSvc.getEventRefreshTokenGetUrl();
        const response = await this.get<IGetTokenResponse>(path, 'refreshEventToken');
        const getEventTokenResponse = this.convertToEventTokenResponseAndSet(eventId, response);
        return getEventTokenResponse;
    }

    convertToEventTokenResponseAndSet(eventId: string, response: IGetTokenResponse): IGetEventTokenResponse {
        this.eventTokenResponse = response;
        const accessTokenInfo = this.utilsSvc.getTokenInfo<IEventTokenPayload>(response.access_token);
        const getEventTokenResponse: IGetEventTokenResponse = {
            token: accessTokenInfo.payload
        };
        this.setEventTokenResponse(eventId, this.eventTokenResponse);
        return getEventTokenResponse;
    }

    async getEventPublicToken(eventId: string): Promise<IGetEventTokenResponse> {
        this.lastEventId = eventId;
        const path = this.utilsSvc.getEventPublicTokenGetUrl(eventId);
        const response = await this.get<IGetTokenResponse>(path, 'getEventToken');
        this.eventTokenResponse = response;
        const accessTokenInfo = this.utilsSvc.getTokenInfo<IEventTokenPayload>(response.access_token);
        const getEventTokenResponse: IGetEventTokenResponse = {
            token: accessTokenInfo.payload
        };
        this.setEventTokenResponse(eventId, this.eventTokenResponse);

        return getEventTokenResponse;
    }

    async getEventSettings(eventId: string): Promise<IEventSettings> {
        const path = this.utilsSvc.getEventSettingsGetUrl(eventId);
        const response = await this.get<IEventSettings>(path, 'getEventSettings');
        return response;
    }

    async getEventFields(req: IPagedRequest): Promise<IGetEventFieldsResponse> {
        const path = this.utilsSvc.getEventFieldsGetUrl();
        const response = await this.getWithQuery<IGetEventFieldsResponse>(
            path, this.utilsSvc.sanitizePagedRequest(req), 'getEventFields'
        );
        return response;
    }

    async getAllEventFields(): Promise<IField[]> {
        const fields = await this.getAllPagedItems(
            req => this.getEventFields(req),
            res => res.fields
        );
        return fields;
    }

    async createEventParticipant(participant: any, fieldMode: FieldMode): Promise<ICreateEventParticipantResponse> {
        const path = this.utilsSvc.getEventParticipantCreateUrl();
        const response = await this.postWithQuery<ICreateEventParticipantResponse>(
            path, participant, { field_mode: fieldMode } as IFieldModeQuery, 'createEventParticipant'
        );
        return response;
    }

    async updateEventParticipant(participant: any, fieldMode: FieldMode): Promise<IUpdateEventParticipantResponse> {
        const path = this.utilsSvc.getEventParticipantUpdateUrl();
        const response = await this.patchWithQuery<IUpdateEventParticipantResponse>(
            path, participant, { field_mode: fieldMode } as IFieldModeQuery, 'updateEventParticipant'
        );
        return response;
    }

    async getAllEventParticipantFields(): Promise<IField[]> {
        const fields = await this.getAllPagedItems(
            req => this.getEventParticipantFields(req),
            res => res.fields
        );
        return fields;
    }

    async getEventParticipantFields(req: IPagedRequest): Promise<IGetEventParticipantFieldsResponse> {
        const path = this.utilsSvc.getEventParticipantFieldsGetUrl();
        const response = await this.getWithQuery<IGetEventParticipantFieldsResponse>(
            path, this.utilsSvc.sanitizePagedRequest(req), 'getEventParticipantFields'
        );
        return response;
    }

    async getLoginPage(eventId: string): Promise<IEventPageResponse> {
        const path = this.utilsSvc.getLoginPageGetPath(eventId);
        const result = await this.get<IEventPageResponse>(path, 'getLoginPage');
        return result;
    }

    async sendEmailAddress(email: string): Promise<any> {
        const path = this.utilsSvc.getSendEmailAddressPath();
        const body = { email: email };
        const response = await this.post<void>(
            path, body, 'sendEmailAdress'
        );
        return response;
    }

    async impersonate(email?: string): Promise<IImpersonateResponse> {
        const path = this.utilsSvc.getIdentityImpersonateUrl();
        const payload = { email: email };
        const response = await this.post<IImpersonateResponse>(path, payload, 'impersonate');
        return response;
    }

    getLinkTokenInfo(linkToken: string): ITokenInfo<ILinkTokenPayload> {
        return this.utilsSvc.getTokenInfo<ILinkTokenPayload>(linkToken);
    }

    async getIdentityTokenFromLinkToken(linkToken: string): Promise<IGetIdentityTokenResponse> {
        // First check in local storage - if we have identity token for the email stored in linkToken
        // use it instead of requesting new one. This is needed because linkToken can be used only once
        // but registration link should be available for the user to click on it many times
        let linkTokenInfo: ITokenInfo<ILinkTokenPayload>;
        try {
            linkTokenInfo = this.getLinkTokenInfo(linkToken);
        } catch (err) {
            throw Error('ERROR:InvalidToken');
        }

        // const storedIdentityToken = this.getStoredIdentityToken();
        // if (storedIdentityToken) {
        //     this.identityTokenResponse = storedIdentityToken.tokenResponse;
        //     const storedAccessTokenInfo = this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(
        //         storedIdentityToken.tokenResponse.access_token
        //     );
        //     const storedGetTokenResponse: IGetIdentityTokenResponse = {
        //         token: storedAccessTokenInfo.payload
        //     };
        //     return storedGetTokenResponse;
        // }

        // There is no such identity token for the email specified in linkToken - obtain one from the server
        const path = this.utilsSvc.getIdentityTokenFromLinkTokenGetUrl();
        const body = this.utilsSvc.formUrlEncode({
            access_token: linkToken
        });
        const response = await this.postWithHeaders<IGetTokenResponse>(
            path, body, this.formUrlEncodedHeader, null, 'getIdentityTokenFromLinkToken'
        );
        const email = this.getUsernameFromEventToken(response.access_token);
        const getTokenResponse = this.processTokenResponse(email, response);
        return getTokenResponse;
    }


    async getIdentityToken(req: IGetIdentityTokenRequest, getNew: boolean): Promise<IGetIdentityTokenResponse> {
        if (!getNew) {
            const storedIdentityToken = this.getStoredIdentityToken();
            if (storedIdentityToken) {
                this.identityTokenResponse = storedIdentityToken.tokenResponse;
                const storedAccessTokenInfo = this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(
                    storedIdentityToken.tokenResponse.access_token
                );
                const storedGetTokenResponse: IGetIdentityTokenResponse = {
                    token: storedAccessTokenInfo.payload
                };
                return storedGetTokenResponse;
            }
        }
        const path = this.utilsSvc.getIdentityTokenGetUrl();
        const body = this.utilsSvc.formUrlEncode({
            username: req.email,
            password: req.registrationCode,
            client_id: this.config.client_id,
            grant_type: 'password'
        });
        const response = await this.postWithHeaders<IGetTokenResponse>(
            path, body, this.formUrlEncodedHeader, null, 'getIdentityToken'
        );
        const getTokenResponse = this.processTokenResponse(req.email, response);
        return getTokenResponse;
    }

    processTokenResponse(email: string, response: IGetTokenResponse): IGetIdentityTokenResponse {
        this.identityTokenResponse = response;
        const accessTokenInfo = this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(response.access_token);
        const getTokenResponse: IGetIdentityTokenResponse = {
            token: accessTokenInfo.payload
        };
        const identityToken: IStoredIdentityToken = { email: email, tokenResponse: this.identityTokenResponse };
        this.storage.setItem(this.identityTokenStorageKeyName, JSON.stringify(identityToken));
        return getTokenResponse;
    }

    saveStoredIdentityTokenToStorage(storedIdentityToken: IStoredIdentityToken): void {
        this.storage.setItem(this.identityTokenStorageKeyName, JSON.stringify(storedIdentityToken));
    }

    async getAllPagedItems<TReq extends IPagedRequest, TRes extends IPagedResponse>(
        loadFunc: (request: TReq) => Promise<TRes>,
        responseArrayKeySelector: (response: TRes) => any[],
        requestForTheLoadFunc?: TReq
    ): Promise<any[]> {
        const result: any[] = [];
        const request: TReq = requestForTheLoadFunc || { limit: 50, offset: 0 } as TReq;
        let itemsCount = 0;
        let hasMoreItems: boolean;
        do {
            const response = await loadFunc(request);
            const responseArray = responseArrayKeySelector(response);
            itemsCount = responseArray ? responseArray.length : 0;
            if (itemsCount > 0) {
                result.push(...responseArray);
                request.offset += itemsCount;
            }
            hasMoreItems = (itemsCount > 0 && request.offset < response.object_count);
        } while (hasMoreItems);
        return result;
    }

    clearLastEventId(): void {
        this.lastEventId = '';
    }

    getUsernameFromEventToken(eventAccessToken: string): string {
        const tokenInfo = this.utilsSvc.getTokenInfo<IEventTokenPayload>(eventAccessToken);
        return tokenInfo.payload.username;
    }

    getCurrentEventAccessTokenPayload(): IEventTokenPayload {
        return this.getEventAccessTokenPayload(this.eventTokenResponse?.access_token);
    }

    getEventAccessTokenPayload(eventAccessToken: string): IEventTokenPayload {
        if (!eventAccessToken) {
            return null;
        }
        const tokenInfo = this.utilsSvc.getTokenInfo<IEventTokenPayload>(eventAccessToken);
        return tokenInfo.payload;
    }

    removeStoredIdentityToken(): void {
        this.storage.removeItem(this.identityTokenStorageKeyName);
    }

    setIdentityTokenResponse(tokenResponse: IGetTokenResponse): void {
        this.identityTokenResponse = tokenResponse;
    }

    removeStoredEventToken(eventId: string): void {
        let currentStoredTokens = this.getStoredEventTokens();
        currentStoredTokens = currentStoredTokens.filter(x => x.eventId !== eventId);
        this.storage.setItem(this.eventTokensStorageKeyName, JSON.stringify(currentStoredTokens));
    }

    setEventTokenResponse(eventId: string, eventTokenResponse: IGetTokenResponse): void {
        // Use that token from now on
        this.lastEventId = eventId;
        this.authorizationHeaderValue = this.utilsSvc.getAuthorizationHeaderFromAccessTokenResponse(eventTokenResponse);
        // Save it in local storage
        const currentEventTokens = this.getStoredEventTokens();
        const currentIndex = currentEventTokens.findIndex(x => x.eventId === eventId);
        if (currentIndex >= 0) {
            currentEventTokens[currentIndex].tokenResponse = eventTokenResponse;
        } else {
            currentEventTokens.push({ eventId: eventId, tokenResponse: eventTokenResponse });
        }
        this.eventTokenResponse = eventTokenResponse;
        this.storage.setItem(this.eventTokensStorageKeyName, JSON.stringify(currentEventTokens));
    }

    subscribeToErrorNotifications(observer: PartialObserver<IErrorNotification>): Subscription {
        return this.errorSubject.subscribe(observer);
    }

    subscribeToSuccessNotifications(observer: PartialObserver<ISuccessNotification>): Subscription {
        return this.successSubject.subscribe(observer);
    }

    private async get<T>(path: string, caller: string, ignoreHttpErrorsList?: number[]): Promise<T> {
        const req = this.createRequest('GET', path);
        return this.request(req, caller, ignoreHttpErrorsList);
    }

    private async getPaged<T>(path: string, pagedRequest: IPagedRequest, caller: string): Promise<T> {
        const response = await this.getWithQuery<T>(path, this.utilsSvc.sanitizePagedRequest(pagedRequest), caller);
        return response;
    }

    private async getWithQuery<T>(path: string, queryParams: any, caller: string): Promise<T> {
        const req = this.createRequest('GET', path, null, queryParams);
        return this.request(req, caller);
    }

    private async getWithHeaders<T>(path: string, headers: any, caller: string, ignoreHttpErrorsList?: number[]): Promise<T> {
        const req = this.createRequest('GET', path, null, null, headers);
        return this.request(req, caller, ignoreHttpErrorsList);
    }

    private async getAny<Blob>(path: string, caller: string, ignoreHttpErrorsList?: number[], queryParams?: any): Promise<Blob> {
        const req = this.createRequest('GET', path, null, queryParams, null, 'blob');
        const blob = await this.request<Blob>(req, caller, ignoreHttpErrorsList);
        return blob;
    }

    private async put<T>(path: string, body: any, caller: string): Promise<T> {
        const req = this.createRequest('PUT', path, body);
        return this.request(req, caller);
    }

    private async putWithQuery<T>(path: string, body: any, queryParams: any, caller: string): Promise<T> {
        const req = this.createRequest('PUT', path, body, queryParams);
        return this.request(req, caller);
    }

    private async post<T>(path: string, body: any, caller: string, ignoreHttpErrorsList?: number[]): Promise<T> {
        const req = this.createRequest('POST', path, body);
        return this.request(req, caller, ignoreHttpErrorsList);
    }

    private async postWithHeaders<T>(path: string, body: any, headers: any, queryParams: any, caller: string): Promise<T> {
        const req = this.createRequest('POST', path, body, queryParams, headers);
        return this.request(req, caller);
    }

    private async postWithQuery<T>(path: string, body: any, queryParams: any, caller: string): Promise<T> {
        const req = this.createRequest('POST', path, body, queryParams);
        return this.request(req, caller);
    }

    private async patch<T>(path: string, body: any, caller: string): Promise<T> {
        const req = this.createRequest('PATCH', path, body);
        return this.request(req, caller);
    }

    private async patchWithQuery<T>(path: string, body: any, queryParams: any, caller: string): Promise<T> {
        const req = this.createRequest('PATCH', path, body, queryParams);
        return this.request(req, caller);
    }

    private async request<T>(req: HttpRequest<any>, caller: string, ignoreHttpErrorsList?: number[]): Promise<T> {
        let result: T;
        try {
            this.lastRequestTime = this.utilsSvc.getNow();
            const promise = this.http.request(req).toPromise() as Promise<HttpEvent<T>>;
            const httpEvent = await promise;
            const httpResponse = httpEvent as HttpResponse<T>;
            result = httpResponse.body as T;
            const tokenResult = result as any as IEventTokenResponse;
            if (this.lastEventId && tokenResult && tokenResult.token && tokenResult.token.access_token && tokenResult.token.token_type) {
                this.setEventTokenResponse(this.lastEventId, tokenResult.token);
            }
            this.successSubject.next({ url: req.urlWithParams, method: req.method, body: req.body, result: result, caller: caller });
        } catch (err) {
            const errObj = this.utilsSvc.createError(err);
            let shouldIgnoreError = false;
            if (errObj.communicationError && ignoreHttpErrorsList && ignoreHttpErrorsList.indexOf(errObj.communicationError.status) >= 0) {
                shouldIgnoreError = true;
            }
            if (!shouldIgnoreError) {
                this.errorSubject.next({ error: errObj, url: req.urlWithParams, method: req.method, body: req.body, caller: caller });
                throw errObj;
            }
        }
        return result;
    }

    private createRequest(
        method: RequestMethod,
        url: string,
        body?: any,
        queryParams?: { [name: string]: string | string[] },
        headers?: { [name: string]: string | string[] },
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'
    ): HttpRequest<any> {
        const req = this.utilsSvc.createRequest(
            method, url, this.authorizationHeaderValue, body, queryParams, headers, responseType
        );
        return req;
    }

    private filterOutIdentityTokensAboutToExpire(storedIdentityTokens: IStoredIdentityToken[]): IStoredIdentityToken[] {
        // Remove all that are about to expire
        const filteredStoredTokens = storedIdentityTokens.filter(x => {
            const identityTokenPayload = this.utilsSvc.getTokenInfo<IIdentityTokenPayload>(x.tokenResponse.access_token);
            return !this.isTokenAboutToExpire(identityTokenPayload.payload.exp);
        });
        return filteredStoredTokens;
    }

    private filterOutEventTokensAboutToExpireOrAreForSpecificPage(storedEventTokens: IStoredEventToken[]): IStoredEventToken[] {
        // Remove all that are about to expire
        const filteredStoredTokens = storedEventTokens.filter(x => {
            try {
                const eventTokenPayload = this.utilsSvc.getTokenInfo<IEventTokenPayload>(x.tokenResponse.access_token);
                const isAboutToExpire = this.isTokenAboutToExpire(eventTokenPayload.payload.exp);
                const isForSpecificPage = this.isEventTokenForSpecificPage(eventTokenPayload.payload);
                return !isAboutToExpire && !isForSpecificPage;
            } catch (err) {
                // The token info cannot be retrieved (probably corrupted token string) - do not use it
                return false;
            }
        });
        return filteredStoredTokens;
    }

    private getStoredLocalStorageTokens<T>(key: string): T[] {
        try {
            const keyValue = this.storage.getItem(key);
            if (!keyValue) {
                return [];
            }
            return JSON.parse(keyValue) as T[];
        } catch (err) {
            return [];
        }
    }

    private getStoredIdentityTokens(): IStoredIdentityToken[] {
        return this.getStoredLocalStorageTokens(this.identityTokensStorageKeyName);
    }

    private getStoredEventTokens(): IStoredEventToken[] {
        return this.getStoredLocalStorageTokens<IStoredEventToken>(this.eventTokensStorageKeyName);
    }

    getStoredIdentityToken(): IStoredIdentityToken | null {
        const storeValue = this.storage.getItem(this.identityTokenStorageKeyName);
        if (storeValue) {
            try {
                return JSON.parse(storeValue) as IStoredIdentityToken;
            } catch (err) {
                return null;
            }
        } else {
            return null;
        }
    }

    getStoredEventToken(eventId: string): IStoredEventToken | null {
        const currentEventTokens = this.getStoredLocalStorageTokens<IStoredEventToken>(this.eventTokensStorageKeyName);
        const eventToken = currentEventTokens.find(x => x.eventId === eventId);
        return eventToken;
    }

    private isTokenAboutToExpire(tokenPayloadExp: number): boolean {
        const now = this.utilsSvc.getNow();
        const tokenPayloadExpMs = tokenPayloadExp * 1000;
        const msLeftToExpiration = tokenPayloadExpMs - now;
        return msLeftToExpiration < this.maxTokenExpirationToleranceMs;
    }

    private isIdentityTokenForSpecificPage(identityTokenPayload: IIdentityTokenPayload): boolean {
        return !!identityTokenPayload.page_id;
    }

    private isEventTokenForSpecificPage(eventTokenPayload: IEventTokenPayload): boolean {
        return !!eventTokenPayload.page_id;
    }

    async getAllScripts(): Promise<any[]> {
        const path = this.utilsSvc.getScriptsGetAllUrl();
        const response = await this.get<any[]>(path, 'getAllScriptsPage', [404]);
        return response;
    }
}
